import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import extractText from 'flarum/common/utils/extractText';

import { MEETUPS_TAG_SLUG } from '../utils/routeState';
import { fetchMeetups, formatMeetupDate, meetupUrl } from '../utils/meetups';
import type { MeetupCard } from '../utils/meetups';

/**
 * `/encuentros` en el SPA — gemelo cliente de `Umbral\Comunidad\Content\Encuentros`.
 *
 * DOS SECCIONES Y ETIQUETADAS, que es el punto de la pagina. Arriba, las fichas de lo que YA
 * PASO (D6): galeria, texto, una URL propia. Abajo, la puerta a lo que PODRIA pasar: el
 * formulario de propuesta (M6) y la categoria de hilos. Sin esa division, una propuesta y un
 * acta se leen igual — que es exactamente lo que este paquete vino a separar.
 *
 * La lista NO se duplica en el payload del foro (a diferencia del catalogo de `/recursos`):
 * cambia cada vez que el equipo publica y paginarla desde `app.forum` obligaria a servirla
 * entera en cada carga del foro. Se pide por API, que ademas es lo que permite el «Ver mas».
 */
export default class EncuentrosPage extends Page {
  /** `null` = cargando; `[]` = cargado y vacio. Se pintan distinto. */
  cards: MeetupCard[] | null = null;

  total = 0;
  failed = false;
  loadingMore = false;

  oninit(vnode: any) {
    super.oninit(vnode);

    app.setTitle(extractText(this.t('meta_title')));
    app.setTitleCount(0);

    this.load();
  }

  view() {
    return (
      <div className="CmMeetups-container container">
        <div className="CmMeetups">
          <header className="CmMeetups-hero">
            <h1 className="CmMeetups-title">{this.t('title')}</h1>
            <p className="CmMeetups-intro">{this.t('intro')}</p>
          </header>

          <section className="CmMeetups-section" data-cm-meetups-past="1">
            <h2 className="CmMeetups-sectionTitle">{this.t('past_title')}</h2>
            {this.past()}
          </section>

          {/*
            SOLO ARCHIVO desde PKG-FORO-PRODUCT-GATES · decision C.

            Aqui vivia una seccion «Propon el proximo encuentro» con su boton al formulario.
            Se retira de esta pagina —y de la home— porque proponer deja de ser una accion de
            la comunidad abierta: pasa a n8n Chile (ver `N8nChilePage` y `addProposeCta`). Lo
            que queda es lo que la pagina siempre fue de verdad: el registro de lo ocurrido.

            Se conserva UNA salida, el enlace a la conversacion, porque el archivo sin puerta a
            la conversacion es un museo. Quien no tiene sesion se encuentra el muro de la
            decision B, que le pide entrar y le devuelve aqui.
          */}
          <section className="CmMeetups-section CmMeetups-conversation" data-cm-meetups-conversation="1">
            {this.categoryLink()}
          </section>
        </div>
      </div>
    );
  }

  /**
   * El enlace a la categoria de hilos.
   *
   * CON SESION va por el router de Mithril (`Link`), que es navegacion instantanea dentro del
   * SPA. SIN SESION va como ancla normal, A PROPOSITO: el muro de `/t/encuentros` lo pone el
   * servidor (`Forum\MeetupsFeedLoginWall`), y una navegacion del router jamas llega al
   * servidor — el invitado se colaria en el feed por la puerta del cliente. Un `<a>` fuerza la
   * peticion completa, el 302 y el modal de Acceder con vuelta a este mismo feed.
   */
  categoryLink() {
    const href = app.route('tag', { tags: MEETUPS_TAG_SLUG });
    const label = this.t('category_link');

    if (!app.session.user) {
      return (
        <a className="CmMeetups-conversationLink" href={href} data-cm-wall="1">
          {label}
        </a>
      );
    }

    return (
      <Link className="CmMeetups-conversationLink" href={href}>
        {label}
      </Link>
    );
  }

  past() {
    if (this.failed) {
      return (
        <p className="CmMeetups-error" data-cm-meetups-error="1">
          {this.t('failed')}
        </p>
      );
    }

    if (this.cards === null) return <LoadingIndicator />;

    if (!this.cards.length) {
      return (
        <div className="CmMeetups-empty" data-cm-meetups-empty="1">
          <b>{this.t('empty_title')}</b>
          <p>{this.t('empty_body')}</p>
        </div>
      );
    }

    return [
      <div className="CmMeetups-grid">{this.cards.map((card) => this.card(card))}</div>,
      this.cards.length < this.total ? (
        <Button className="Button CmMeetups-more" loading={this.loadingMore} disabled={this.loadingMore} onclick={() => this.load(true)}>
          {this.t('load_more')}
        </Button>
      ) : null,
    ];
  }

  card(card: MeetupCard) {
    return (
      <Link className="CmMeetupCard" href={meetupUrl(card.slug)} data-cm-meetup={card.slug}>
        <span className="CmMeetupCard-media">
          {card.cover ? (
            <img
              src={card.cover.url}
              /* Vacio cuando no hay pie: el titulo va justo debajo y un lector de pantalla no
                 gana nada oyendo dos veces lo mismo. */
              alt={card.cover.caption || ''}
              width={card.cover.width || undefined}
              height={card.cover.height || undefined}
              loading="lazy"
            />
          ) : (
            <span className="CmMeetupCard-noPhoto" aria-hidden="true" />
          )}
        </span>
        <span className="CmMeetupCard-body">
          <span className="CmMeetupCard-meta">
            <time>{formatMeetupDate(card.heldOn)}</time>
            {card.place ? [<span>·</span>, <span>{card.place}</span>] : null}
            {/* Solo un admin ve fichas sin publicar; el resto no las recibe. */}
            {!card.published ? <span className="CmMeetupCard-draft">{this.t('draft_badge')}</span> : null}
          </span>
          <b className="CmMeetupCard-title">{card.title}</b>
          <span className="CmMeetupCard-excerpt">{card.excerpt}</span>
          <span className="CmMeetupCard-counts">
            {card.photoCount ? (
              <span>{app.translator.trans('umbral-comunidad.forum.encuentros.photo_count', { count: card.photoCount })}</span>
            ) : null}
            <span>{app.translator.trans('umbral-comunidad.forum.encuentros.like.count', { count: card.likeCount })}</span>
          </span>
        </span>
      </Link>
    );
  }

  load(more = false) {
    if (more) this.loadingMore = true;
    else this.failed = false;

    m.redraw();

    fetchMeetups({ offset: more && this.cards ? this.cards.length : 0 })
      .then((data) => {
        // Al paginar se CONCATENA; en la primera carga se reemplaza. Sin esta distincion el
        // «Ver mas» sustituiria la pagina anterior por la siguiente en vez de anadirla.
        this.cards = more && this.cards ? this.cards.concat(data.meetups) : data.meetups;
        this.total = data.total;
        this.loadingMore = false;
        m.redraw();
      })
      .catch(() => {
        this.loadingMore = false;

        // Un fallo al paginar deja lo ya cargado en pantalla: borrarlo castigaria al lector por
        // haber pedido mas.
        if (!more) this.failed = true;

        m.redraw();
      });
  }

  t(key: string) {
    return app.translator.trans('umbral-comunidad.forum.encuentros.' + key);
  }
}
